import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlass, MusicNotes, Play, Pause, Check, X } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * Music picker sheet. Browses the curated catalog with mood chips,
 * one-tap preview, and confirms with the selected track id.
 *
 * Props:
 *   - selectedId: currently attached music_id (or null)
 *   - onClose:   () => void
 *   - onSelect:  (musicEntry) => void   passes the full entry, parent stores .id
 */
export default function MusicPicker({ selectedId, onClose, onSelect }) {
  const [moods, setMoods] = useState([]);
  const [mood, setMood] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  // Fetch when mood/q change
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/music/catalog?q=${encodeURIComponent(q)}&mood=${encodeURIComponent(mood)}`);
        setItems(data.items);
        if (data.moods) setMoods(data.moods);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [q, mood]);

  // Cleanup audio on unmount
  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  const togglePreview = (item) => {
    if (audioRef.current && playingId === item.id) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(item.audio_url);
    a.volume = 0.7;
    a.play().catch(() => { /* autoplay blocked or 404 */ });
    a.onended = () => setPlayingId(null);
    audioRef.current = a;
    setPlayingId(item.id);
  };

  const choose = (item) => {
    if (audioRef.current) audioRef.current.pause();
    setPlayingId(null);
    onSelect?.(item);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[80] bg-ink/80 backdrop-blur-sm flex items-end sm:items-center justify-center"
        data-testid="music-picker"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 60 }}
          animate={{ y: 0 }}
          exit={{ y: 60 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="w-full sm:max-w-lg bg-ink border-t sm:border border-white/10 rounded-t-3xl sm:rounded-3xl max-h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 pt-5">
            <div>
              <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-black">Music</p>
              <h3 className="text-cream text-2xl font-black tracking-tighter">Pick a sound</h3>
            </div>
            <button onClick={onClose} className="w-10 h-10 rounded-full crystal-glass text-cream flex items-center justify-center" data-testid="music-close">
              <X size={18} />
            </button>
          </div>

          <div className="px-5 mt-4">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-cream/40" />
              <input
                data-testid="music-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, artist, mood"
                className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-4 py-2.5 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-papaya"
              />
            </div>

            <div className="flex gap-1.5 mt-3 overflow-x-auto no-scrollbar">
              <button data-testid="music-mood-all" onClick={() => setMood("")} className={`px-3 py-1.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-black ${mood === "" ? "bg-papaya text-ink" : "bg-white/5 text-cream/60"}`}>All</button>
              {moods.map((m) => (
                <button key={m} data-testid={`music-mood-${m}`} onClick={() => setMood(mood === m ? "" : m)} className={`px-3 py-1.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-black flex-shrink-0 ${mood === m ? "bg-papaya text-ink" : "bg-white/5 text-cream/60"}`}>{m}</button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 mt-4 pb-6">
            {items.length === 0 ? (
              <p className="px-2 text-cream/40 text-sm">No tracks match.</p>
            ) : (
              <div className="space-y-1.5">
                {items.map((m) => {
                  const isSelected = m.id === selectedId;
                  const isPlaying = playingId === m.id;
                  return (
                    <div
                      key={m.id}
                      data-testid={`music-item-${m.id}`}
                      className={`flex items-center gap-3 p-2.5 rounded-2xl border transition-colors ${isSelected ? "bg-papaya/10 border-papaya/40" : "bg-white/5 border-white/10 hover:border-white/20"}`}
                    >
                      <button
                        onClick={() => togglePreview(m)}
                        data-testid={`music-preview-${m.id}`}
                        className="w-11 h-11 rounded-xl bg-gradient-to-br from-papaya/30 via-jam-green/20 to-jam-gold/20 flex items-center justify-center text-lg flex-shrink-0 relative"
                      >
                        <span aria-hidden>{m.cover_emoji}</span>
                        <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-ink flex items-center justify-center border border-papaya">
                          {isPlaying ? <Pause size={11} weight="fill" className="text-papaya" /> : <Play size={11} weight="fill" className="text-papaya" />}
                        </span>
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-cream font-bold text-sm truncate">{m.title}</p>
                        <p className="text-cream/50 text-[11px] truncate">{m.artist} · {m.mood}</p>
                      </div>
                      <button
                        onClick={() => choose(m)}
                        data-testid={`music-pick-${m.id}`}
                        className={`px-3 py-1.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-black ${isSelected ? "bg-papaya text-ink" : "bg-white/10 text-cream"}`}
                      >
                        {isSelected ? <Check size={12} weight="bold" /> : "Use"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
